import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Scales,
  User,
  Buildings,
  ArrowRight,
  CircleNotch,
  Check,
  GoogleLogo,
  MicrosoftOutlookLogo,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { startSession, type WorkspaceType, type OnboardingPlan } from '@/lib/onboardingApi';
import { beginAuthentication, fetchAuthProviders } from '@/lib/authJourney';
import { isValidName, isValidEmail, sanitizeName, NAME_ERROR, EMAIL_ERROR } from '@/lib/validators';
import { BackButton } from '@/components/shared/BackButton';

// ── Plan config ───────────────────────────────────────────────────────────────

const SOLO_PLAN = {
  id: 'solo' as OnboardingPlan,
  label: 'Single',
  price: '£69.99',
  period: '/month',
  description: 'For a single self-employed barrister operating independently',
  features: ['Full matter management', 'Fee notes & billing', 'Diary & hearings', 'Document vault', 'Client portal'],
};

const CHAMBERS_PLANS: { id: OnboardingPlan; label: string; price: string; period: string; description: string; features: string[] }[] = [
  {
    id: 'starter',
    label: 'Starter',
    price: '£199',
    period: '/month',
    description: 'Up to 3 barristers',
    features: ['All Single features', 'Clerk management', 'Allocation briefs', 'Multi-barrister diary', 'Chambers reporting'],
  },
  {
    id: 'professional',
    label: 'Professional',
    price: '£300',
    period: '/month',
    description: 'Up to 7 barristers',
    features: ['All Starter features', 'Advanced analytics', 'Priority support', 'Custom branding', 'API access'],
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'Tell us your requirements — we’ll tailor a plan to match',
    features: ['All Professional features', 'Dedicated account manager', 'SLA guarantee', 'Custom integrations', 'On-premise option'],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'type' | 'plan' | 'details'>('type');
  const [workspaceType, setWorkspaceType] = useState<WorkspaceType>('chambers');
  const [plan, setPlan] = useState<OnboardingPlan>('starter');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organisationName: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingAccount, setExistingAccount] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ firstName?: string; lastName?: string; email?: string }>({});
  const [touched, setTouched] = useState<{ firstName?: boolean; lastName?: boolean; email?: boolean }>({});

  // Which social-login providers the server has configured — so we only show a "Continue with X"
  // button that actually works. Solo trials can start instantly via OAuth (no form, no activation
  // email); the OAuth create_account journey provisions a solo workspace, so it's offered on the
  // SOLO path only.
  const { data: providers } = useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: fetchAuthProviders,
    staleTime: 5 * 60 * 1000,
  });
  const hasGoogle = providers?.google ?? false;
  const hasMicrosoft = providers?.microsoft ?? false;
  const showOAuthTrial = workspaceType === 'solo' && (hasGoogle || hasMicrosoft);

  // Live field validation — runs on change (if already touched) and on blur.
  function validateField(field: 'firstName' | 'lastName' | 'email', value: string) {
    let err: string | undefined;
    if (field === 'email') err = isValidEmail(value) ? undefined : EMAIL_ERROR;
    else err = isValidName(value) ? undefined : NAME_ERROR;
    setFieldErrors((fe) => ({ ...fe, [field]: err }));
  }

  function handleTypeSelect(type: WorkspaceType) {
    setWorkspaceType(type);
    if (type === 'solo') setPlan('solo');
    else setPlan('starter');
    setStep('plan');
  }

  function handlePlanSelect(p: OnboardingPlan) {
    setPlan(p);
    setStep('details');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setExistingAccount(false);
    setFieldErrors({});
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('Please complete all required fields.');
      return;
    }
    if (workspaceType === 'chambers' && !form.organisationName.trim()) {
      setError('Chambers name is required.');
      return;
    }
    const fe: { firstName?: string; lastName?: string; email?: string } = {};
    if (!isValidName(form.firstName)) fe.firstName = NAME_ERROR;
    if (!isValidName(form.lastName)) fe.lastName = NAME_ERROR;
    if (!isValidEmail(form.email)) fe.email = EMAIL_ERROR;
    if (Object.keys(fe).length > 0) {
      setFieldErrors(fe);
      return;
    }
    setLoading(true);
    try {
      const { token } = await startSession({
        workspaceType,
        plan,
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        organisationName: workspaceType === 'chambers' ? form.organisationName.trim() : undefined,
      });
      navigate(`/onboarding/payment/${token}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setError(msg ?? 'Something went wrong. Please try again.');
      // An existing account is a "you're already here" moment, not a failure — route them to sign in.
      setExistingAccount(/already exists/i.test(msg ?? ''));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {/* Nav */}
      <header className="border-b border-[#e5e3de] bg-white px-8 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-[#1a2f4e]">
              <Scales className="h-4 w-4 text-white" weight="duotone" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-[#1a1915]">Jurivoxa</span>
          </Link>
          <Link to="/login" className="text-sm text-[#6b6961] hover:text-[#1a1915]">
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12">
        {/* Step: workspace type */}
        {step === 'type' && (
          <div>
            <div className="mb-10 text-center">
              <h1 className="text-2xl font-semibold text-[#1a1915]">How will you use Jurivoxa?</h1>
              <p className="mt-2 text-sm text-[#6b6961]">Choose the workspace type that fits your practice.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <button
                onClick={() => handleTypeSelect('solo')}
                className="group rounded-lg border-2 border-[#e5e3de] bg-white p-8 text-left transition-all hover:border-[#1a2f4e] hover:shadow-sm"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#f0ede8] group-hover:bg-[#1a2f4e]/10">
                  <User className="h-6 w-6 text-[#1a2f4e]" weight="duotone" />
                </div>
                <h2 className="mb-1 font-semibold text-[#1a1915]">Solo Barrister</h2>
                <p className="text-sm text-[#6b6961]">
                  Self-employed barrister operating independently. Your own matters, diary, fees, and client portal.
                </p>
              </button>

              <button
                onClick={() => handleTypeSelect('chambers')}
                className="group rounded-lg border-2 border-[#e5e3de] bg-white p-8 text-left transition-all hover:border-[#1a2f4e] hover:shadow-sm"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#f0ede8] group-hover:bg-[#1a2f4e]/10">
                  <Buildings className="h-6 w-6 text-[#1a2f4e]" weight="duotone" />
                </div>
                <h2 className="mb-1 font-semibold text-[#1a1915]">Chambers</h2>
                <p className="text-sm text-[#6b6961]">
                  A set of chambers with multiple barristers, clerks, and shared administration.
                </p>
              </button>
            </div>
          </div>
        )}

        {/* Step: plan selection */}
        {step === 'plan' && (
          <div>
            <div className="mb-8 text-center">
              <BackButton
                onClick={() => setStep('type')}
                className="mb-4 text-[#6b6961] hover:text-[#1a1915]"
              />
              <h1 className="text-2xl font-semibold text-[#1a1915]">
                {workspaceType === 'solo' ? 'Solo Barrister Plan' : 'Choose a plan'}
              </h1>
              {/*
                "No credit card required" is now unconditionally true — the card step this page led
                to has been removed. The "30-day trial" claim is not: the email/password path this
                screen continues into creates the workspace with `status: 'active'` and never sets
                `trialEndsAt` (apps/api/src/lib/workspace-provisioning.ts), so no trial window
                exists to be 30 days long. The 15-day figure on the OAuth button below IS accurate
                — `provisionOAuthSoloTrial` sets both `status: 'trial'` and `trialEndsAt` — which is
                exactly why only this line changes. Trial length for the password path is an open
                commercial question (docs/BILLING-DECISIONS-NEEDED.html Q5), not this fix's to
                answer.
              */}
              <p className="mt-2 text-sm text-[#6b6961]">No credit card required.</p>
            </div>

            {workspaceType === 'solo' ? (
              <div className="mx-auto max-w-sm">
                <div className="rounded-lg border-2 border-[#1a2f4e] bg-white p-8">
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      <h2 className="font-semibold text-[#1a1915]">{SOLO_PLAN.label}</h2>
                      <p className="text-sm text-[#6b6961]">{SOLO_PLAN.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-semibold text-[#1a1915]">{SOLO_PLAN.price}</div>
                      <div className="text-xs text-[#6b6961]">{SOLO_PLAN.period}</div>
                    </div>
                  </div>
                  <ul className="mb-6 space-y-2">
                    {SOLO_PLAN.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#3d3b35]">
                        <Check className="h-4 w-4 flex-shrink-0 text-[#1a2f4e]" weight="bold" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handlePlanSelect('solo')}
                    className="w-full rounded bg-[#1a2f4e] py-2.5 text-sm font-medium text-white hover:bg-[#243d61]"
                  >
                    Get started
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-3">
                {CHAMBERS_PLANS.map((p) => (
                  <div
                    key={p.id}
                    className={`rounded-lg border-2 bg-white p-6 ${plan === p.id ? 'border-[#1a2f4e]' : 'border-[#e5e3de]'}`}
                  >
                    <div className="mb-1 font-semibold text-[#1a1915]">{p.label}</div>
                    <div className="mb-1 text-lg font-semibold text-[#1a1915]">
                      {p.price}<span className="text-sm font-normal text-[#6b6961]">{p.period}</span>
                    </div>
                    <div className="mb-4 text-xs text-[#6b6961]">{p.description}</div>
                    <ul className="mb-6 space-y-1.5">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-xs text-[#3d3b35]">
                          <Check className="h-3.5 w-3.5 flex-shrink-0 text-[#1a2f4e]" weight="bold" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanSelect(p.id)}
                      className="w-full rounded bg-[#1a2f4e] py-2 text-sm font-medium text-white hover:bg-[#243d61]"
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: contact details */}
        {step === 'details' && (
          <div className="mx-auto max-w-lg">
            <div className="mb-8">
              <BackButton
                onClick={() => setStep('plan')}
                className="mb-4 text-[#6b6961] hover:text-[#1a1915]"
              />
              <h1 className="text-2xl font-semibold text-[#1a1915]">Your details</h1>
              <p className="mt-1 text-sm text-[#6b6961]">
                {workspaceType === 'chambers'
                  ? 'You will become the administrator of your chambers workspace.'
                  : 'You will be the owner of your solo practice workspace.'}
              </p>
            </div>

            {/* Solo fast-path: start the 15-day trial instantly with Google/Microsoft — no form,
                no activation email, no card. Provisions the solo workspace and signs the barrister
                straight in (create_account journey). */}
            {showOAuthTrial && (
              <div className="mb-5 rounded-lg border border-[#e5e3de] bg-white p-8">
                <p className="mb-3 text-center text-sm font-medium text-[#1a1915]">
                  Start your 15-day free trial instantly
                </p>
                <div className="space-y-2.5">
                  {hasGoogle && (
                    <button
                      type="button"
                      onClick={() => beginAuthentication({ provider: 'google', intent: 'create_account' })}
                      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded border border-[#d4d0c8] bg-white px-5 text-sm font-medium text-[#1a1915] transition hover:bg-[#faf9f6]"
                    >
                      <GoogleLogo className="h-[18px] w-[18px]" weight="bold" />
                      Continue with Google
                    </button>
                  )}
                  {hasMicrosoft && (
                    <button
                      type="button"
                      onClick={() => beginAuthentication({ provider: 'microsoft', intent: 'create_account' })}
                      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded border border-[#d4d0c8] bg-white px-5 text-sm font-medium text-[#1a1915] transition hover:bg-[#faf9f6]"
                    >
                      <MicrosoftOutlookLogo className="h-[18px] w-[18px]" weight="bold" />
                      Continue with Microsoft
                    </button>
                  )}
                </div>
                <div className="my-5 flex items-center gap-3">
                  <div className="h-px flex-1 bg-[#e5e3de]" />
                  <span className="text-xs font-medium uppercase tracking-wider text-[#9c9990]">or sign up with email</span>
                  <div className="h-px flex-1 bg-[#e5e3de]" />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-[#e5e3de] bg-white p-8">
              {workspaceType === 'chambers' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">Chambers name *</label>
                  <input
                    type="text"
                    value={form.organisationName}
                    onChange={(e) => setForm((f) => ({ ...f, organisationName: e.target.value }))}
                    placeholder="e.g. Smith & Co Chambers"
                    className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                    required
                  />
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">First name *</label>
                  <input
                    type="text"
                    value={form.firstName}
                    maxLength={200}
                    onChange={(e) => { const v = sanitizeName(e.target.value); setForm((f) => ({ ...f, firstName: v })); if (touched.firstName) validateField('firstName', v); }}
                    onBlur={(e) => { setTouched((t) => ({ ...t, firstName: true })); validateField('firstName', e.target.value); }}
                    placeholder="James"
                    className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                    required
                  />
                  {fieldErrors.firstName && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{fieldErrors.firstName}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">Last name *</label>
                  <input
                    type="text"
                    value={form.lastName}
                    maxLength={200}
                    onChange={(e) => { const v = sanitizeName(e.target.value); setForm((f) => ({ ...f, lastName: v })); if (touched.lastName) validateField('lastName', v); }}
                    onBlur={(e) => { setTouched((t) => ({ ...t, lastName: true })); validateField('lastName', e.target.value); }}
                    placeholder="Thornton"
                    className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                    required
                  />
                  {fieldErrors.lastName && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{fieldErrors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">Email address *</label>
                <input
                  type="email"
                  value={form.email}
                  maxLength={255}
                  onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, email: v })); if (touched.email) validateField('email', v); }}
                  onBlur={(e) => { setTouched((t) => ({ ...t, email: true })); validateField('email', e.target.value); }}
                  placeholder="j.thornton@chambers.com"
                  className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                  required
                />
                {fieldErrors.email
                  ? <p className="mt-1 text-xs text-red-700 dark:text-red-300">{fieldErrors.email}</p>
                  : <p className="mt-1 text-xs text-[#9c9990]">Your activation link will be sent here.</p>}
              </div>

              {error && (
                <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  <p>{error}</p>
                  {existingAccount && (
                    <Link to="/login" className="mt-1 inline-block font-medium text-[#1a2f4e] underline">
                      Sign in instead →
                    </Link>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded bg-[#1a2f4e] py-2.5 text-sm font-medium text-white hover:bg-[#243d61] disabled:opacity-60"
              >
                {loading ? (
                  <CircleNotch className="h-4 w-4 animate-spin" />
                ) : (
                  <>Continue to payment <ArrowRight className="h-4 w-4" /></>
                )}
              </button>

              <p className="text-center text-xs text-[#9c9990]">
                By continuing you agree to Jurivoxa's Terms of Service and Privacy Policy.
              </p>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
